import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { MUNICIPIO_SIADAP_ID } from '@/lib/municipios';
import { registarAlteracao, registarAlteracaoDetalhada, type EntidadeAuditada } from '@/utils/auditoria';

export interface UnidadeOrganicaSiadap {
  id: string;
  nome: string;
  tipo: string; // 'unidade' | 'subunidade'
  unidadePaiId: string | null;
}

export interface Funcionario {
  id: string;
  nome: string;
  carreira: string;
  sexo: 'M' | 'F';
  genero: string;
  categoria: string;
  ultimaProgressao: string;
  nivelAtual: number;
  novoNivel2025: number;
  descricaoSubida: string;
  avaliacao: 'Excelente' | 'Muito Bom' | 'Adequado' | 'Inadequado';
  vencimento: number;
  unidadeOrganica: string;
  subunidadeOrganica: string;
  pontosAtuais: number;
  pontosDescontados: number;
  dataEntradaFuncaoPublica?: string;
  excecaoPrepav: boolean;
  dataAlteracaoCategoria?: string;
  categoriaAnterior?: string;
}

interface FuncionariosContextValue {
  funcionarios: Funcionario[];
  unidadesOrganicas: UnidadeOrganicaSiadap[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  addFuncionario: (f: Funcionario) => Promise<void>;
  updateFuncionario: (f: Funcionario, oldId?: string) => Promise<void>;
  deleteFuncionario: (id: string) => Promise<void>;
  reload: () => Promise<void>;
}

const FuncionariosContext = createContext<FuncionariosContextValue | null>(null);

function toFuncionario(row: Record<string, unknown>): Funcionario {
  return {
    id: row.id as string,
    nome: row.nome as string,
    carreira: row.carreira as Funcionario['carreira'],
    sexo: row.sexo as 'M' | 'F',
    genero: (row.genero as string) || (row.sexo as 'M' | 'F') || '',
    categoria: (row.categoria as string) || '',
    ultimaProgressao: row.ultima_progressao as string ?? '',
    nivelAtual: row.nivel_atual as number,
    novoNivel2025: row.novo_nivel_2025 as number,
    descricaoSubida: row.descricao_subida as string ?? '',
    avaliacao: row.avaliacao as Funcionario['avaliacao'],
    vencimento: Number(row.vencimento),
    unidadeOrganica: row.unidade_organica as string ?? '',
    subunidadeOrganica: row.subunidade_organica as string ?? '',
    pontosAtuais: Number(row.pontos_atuais ?? 0),
    pontosDescontados: Number(row.pontos_descontados ?? 0),
    dataEntradaFuncaoPublica: (row.data_entrada_funcao_publica as string) ?? '',
    excecaoPrepav: Boolean(row.excecao_prepav ?? false),
    dataAlteracaoCategoria: (row.data_alteracao_categoria as string) ?? '',
    categoriaAnterior: (row.categoria_anterior as string) ?? '',
  };
}

function toDbRow(f: Funcionario, municipioId?: string) {
  return {
    id: f.id,
    nome: f.nome,
    carreira: f.carreira,
    sexo: f.sexo,
    ultima_progressao: f.ultimaProgressao || null,
    nivel_atual: f.nivelAtual,
    novo_nivel_2025: f.novoNivel2025,
    descricao_subida: f.descricaoSubida,
    avaliacao: f.avaliacao,
    vencimento: f.vencimento,
    unidade_organica: f.unidadeOrganica,
    subunidade_organica: f.subunidadeOrganica,
    pontos_atuais: f.pontosAtuais,
    pontos_descontados: f.pontosDescontados,
    data_entrada_funcao_publica: f.dataEntradaFuncaoPublica || null,
    excecao_prepav: f.excecaoPrepav ?? false,
    data_alteracao_categoria: f.dataAlteracaoCategoria || null,
    categoria_anterior: f.categoriaAnterior || null,
    updated_at: new Date().toISOString(),
    ...(municipioId ? { municipio_id: municipioId } : {}),
  };
}

export function FuncionariosProvider({ children }: { children: ReactNode }) {
  const { user, profileLoading, profile } = useAuth();
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [unidadesOrganicas, setUnidadesOrganicas] = useState<UnidadeOrganicaSiadap[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedRef = useRef(false);

  const municipioId = MUNICIPIO_SIADAP_ID;
  const userEmail = profile?.email ?? null;

  const reload = useCallback(async () => {
    if (!user) {
      setFuncionarios([]);
      setLoading(false);
      return;
    }
    loadedRef.current = true;
    setFuncionarios([]);
    setLoading(true);
    setError(null);
    try {
      const [funcRes, unidRes] = await Promise.all([
        supabase
          .from('funcionarios')
          .select('*')
          .eq('municipio_id', municipioId)
          .order('nome'),
        supabase
          .from('unidades_organicas')
          .select('id, nome, tipo, unidade_pai_id')
          .eq('municipio_id', municipioId)
          .order('ordem'),
      ]);
      if (funcRes.error) {
        setError(funcRes.error.message);
      } else {
        setFuncionarios((funcRes.data ?? []).map(toFuncionario));
      }
      if (!unidRes.error) {
        setUnidadesOrganicas(
          (unidRes.data ?? []).map((r) => ({
            id: r.id as string,
            nome: r.nome as string,
            tipo: r.tipo as string,
            unidadePaiId: r.unidade_pai_id as string | null,
          }))
        );
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  }, [user, municipioId]);

  useEffect(() => {
    if (!user) {
      setFuncionarios([]);
      setLoading(false);
      loadedRef.current = false;
      return;
    }
    if (profileLoading) return;
    if (loadedRef.current) return;
    loadedRef.current = true;
    reload();
  }, [user, profileLoading, reload]);

  async function addFuncionario(f: Funcionario) {
    setSaving(true);
    try {
      const dbRow = toDbRow(f, municipioId);
      const { data, error: err } = await supabase.from('funcionarios').insert(dbRow).select();
      if (err) throw new Error(err.message);

      await registarAlteracao(
        'funcionario' as EntidadeAuditada,
        f.id,
        f.nome,
        'criacao',
        `Funcionário ${f.nome} (Nº ${f.id}) criado.`,
        { municipioId, modulo: 'siadap', userEmail }
      );
      await reload();
    } catch (e: unknown) {
      throw e;
    } finally {
      setSaving(false);
    }
  }

  async function updateFuncionario(f: Funcionario, oldId?: string) {
    setSaving(true);
    try {
      if (oldId && oldId !== f.id) {
        // Alteração do número de funcionário (recria o registo)
        const { error: insertErr } = await supabase.from('funcionarios').insert(toDbRow(f, municipioId));
        if (insertErr) throw new Error(insertErr.message);
        await supabase.from('registos_siadap').update({ funcionario_id: f.id }).eq('funcionario_id', oldId);
        const { error: delErr } = await supabase.from('funcionarios').delete().eq('id', oldId);
        if (delErr) throw new Error(delErr.message);

        await registarAlteracao(
          'funcionario' as EntidadeAuditada,
          f.id,
          f.nome,
          'edicao',
          `Nº de funcionário alterado de ${oldId} para ${f.id}.`,
          { campoAlterado: 'Nº Funcionário', valorAnterior: oldId, valorNovo: f.id, municipioId, modulo: 'siadap', userEmail }
        );
      } else {
        const anterior = funcionarios.find((x) => x.id === f.id);
        const dbAnterior = anterior ? toDbRow(anterior, municipioId) : {};
        const dbNovo = toDbRow(f, municipioId);

        const { error: err } = await supabase.from('funcionarios').update(dbNovo).eq('id', f.id);
        if (err) throw new Error(err.message);

        if (anterior) {
          await registarAlteracaoDetalhada(
            'funcionario' as EntidadeAuditada,
            f.id,
            f.nome,
            'edicao',
            dbAnterior,
            dbNovo,
            { municipioId, modulo: 'siadap', userEmail, camposIgnorados: ['id', 'updated_at', 'municipio_id'] }
          );
        } else {
          await registarAlteracao(
            'funcionario' as EntidadeAuditada,
            f.id,
            f.nome,
            'edicao',
            `Dados do funcionário ${f.nome} (Nº ${f.id}) atualizados.`,
            { municipioId, modulo: 'siadap', userEmail }
          );
        }
      }
      await reload();
    } finally {
      setSaving(false);
    }
  }

  async function deleteFuncionario(id: string) {
    setSaving(true);
    try {
      const func = funcionarios.find((x) => x.id === id);
      const nome = func?.nome ?? id;

      await registarAlteracao(
        'funcionario' as EntidadeAuditada,
        id,
        nome,
        'eliminacao',
        `Funcionário ${nome} (Nº ${id}) eliminado.`,
        { municipioId, modulo: 'siadap', userEmail }
      );

      await supabase.from('registos_siadap').delete().eq('funcionario_id', id).eq('municipio_id', municipioId);
      await supabase.from('historico_progressoes').delete().eq('funcionario_id', id).eq('municipio_id', municipioId);
      const { error: err } = await supabase.from('funcionarios').delete().eq('id', id).eq('municipio_id', municipioId);
      if (err) throw new Error(err.message);
      await reload();
    } finally {
      setSaving(false);
    }
  }

  return (
    <FuncionariosContext.Provider value={{ funcionarios, unidadesOrganicas, loading, saving, error, addFuncionario, updateFuncionario, deleteFuncionario, reload }}>
      {children}
    </FuncionariosContext.Provider>
  );
}

export function useFuncionarios(): FuncionariosContextValue {
  const ctx = useContext(FuncionariosContext);
  if (!ctx) throw new Error('useFuncionarios must be used within FuncionariosProvider');
  return ctx;
}